import React, { useState, useRef, useCallback, forwardRef } from 'react';
import { Button } from '~/ui/components';
import { File as FileIcon, Trash } from '~/ui/assets/icons';
import { message } from 'antd';
import {
  Container,
  UploadArea,
  Title,
  Description,
  FilePreviewContainer,
  FilePreviewItem,
  FileInfo,
  FileName,
  FileSize,
  FileActions,
  RemoveButton,
  Content,
  ButtonWrapper,
  InfoWrapper,
} from './Files.styled';

export type FileType = {
  id: string;
  file: File;
  preview?: string;
  disableRemoveButton?: boolean;
};

export type FilesProps = {
  onFilesSelect?: (files: FileType[]) => void;
  onChange?: (files: any) => void;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // em bytes
  className?: string;
  value?: any;
};

export const Files = forwardRef<HTMLDivElement, FilesProps>(
  (
    {
      onFilesSelect,
      accept = '.pdf, .png, .jpg, .mov, .mp4, .jpeg',
      multiple = true,
      maxFiles,
      maxSize = 20 * 1024 * 1024, // 20MB
      className,
      ...props
    },
    ref,
  ) => {
    const [files, setFiles] = useState<FileType[]>(props?.value || []);
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef<any>(ref);

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    };

    const formatFileTypes = (fileTypes: string) =>
      fileTypes
        .split(',')
        .map((type) => type.trim().replace('.', ''))
        .join(', ');

    const getAcceptedMimeTypes = (accept: string) => {
      const mimeTypeMap: { [key: string]: string } = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        mov: 'video/quicktime',
        mp4: 'video/mp4',
      };

      return accept
        .split(',')
        .map((type) => type.trim().replace('.', ''))
        .map((ext) => mimeTypeMap[ext])
        .filter(Boolean);
    };

    const handleFileSelect = useCallback(
      (selectedFiles?: FileList) => {
        if (!selectedFiles) return;

        const newFiles: FileType[] = [];
        const acceptedTypes = getAcceptedMimeTypes(accept);
        const fileListTransfer = new DataTransfer();

        Array.from(selectedFiles).forEach((file) => {
          let hasError = false;
          let preview: string;
          // Verificar tipo do arquivo
          // Verificar tamanho do arquivo
          if (file.size > maxSize) {
            hasError = true;
            message.warn(`O arquivo ${file.name} excede o tamanho máximo permitido de ${formatFileSize(maxSize)}`);
          }

          // Verificar número máximo de arquivos
          if (maxFiles && files.length + newFiles.length >= maxFiles) {
            hasError = true;
            message.warn(`Você pode fazer upload de no máximo ${maxFiles} arquivos`);
          }

          if (!acceptedTypes.includes(file.type)) {
            hasError = true;
            message.warn(
              `O arquivo ${file.name} não é de um tipo permitido. Tipos aceitos: ${formatFileTypes(accept)}`,
            );
          }

          // Verificar se o arquivo já foi adicionado
          if (files.some((existingFile) => existingFile.file.name === file.name)) {
            hasError = true;
            message.warn(`O arquivo ${file.name} já foi adicionado`);
          }

          // Criar preview para imagens
          if (file.type.startsWith('image/')) {
            preview = URL.createObjectURL(file);
          }

          if (!hasError) {
            fileListTransfer.items.add(file);
            newFiles.push({
              id: `${file.name}`,
              file,
              preview,
            });
          }
        });

        inputRef.current.files = fileListTransfer.files;
        const updatedFiles = [...files, ...newFiles];
        setFiles(updatedFiles);
        props?.onChange(updatedFiles);

        onFilesSelect?.(updatedFiles);
      },
      [files, maxFiles, maxSize, onFilesSelect],
    );

    const handleBrowseClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e?.target as any;

      if (target?.classList?.contains('trash-button')) {
        return;
      }

      if (inputRef?.current) {
        inputRef.current.click();
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    };

    const handleRemoveFile = (id: string) => {
      const updatedFiles = files.filter((file) => file.id !== id);
      setFiles(updatedFiles);

      if (onFilesSelect) {
        onFilesSelect(updatedFiles);
      }

      // Liberar URL de preview para evitar memory leaks
      const fileToRemove = files.find((file) => file.id === id);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      props?.onChange(updatedFiles);
    };

    return (
      <Container className={className}>
        <UploadArea
          isDragging={isDragging}
          onClick={handleBrowseClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Content>
            <InfoWrapper>
              <Title>Anexar arquivos</Title>
              <Description>
                {multiple && maxFiles && `Você pode fazer upload de até ${maxFiles} arquivos. `}
                {multiple
                  ? `Tamanho máximo por arquivo: ${formatFileSize(maxSize)}.`
                  : `Tamanho máximo do arquivo: ${formatFileSize(maxSize)}.`}{' '}
                {accept && `(${formatFileTypes(accept)})`}
              </Description>
            </InfoWrapper>

            <ButtonWrapper>
              <Button rounded variant="outline" fullWidth type="button">
                Anexar
              </Button>
            </ButtonWrapper>
          </Content>

          {files.length > 0 && (
            <FilePreviewContainer>
              {files.map((file) => (
                <FilePreviewItem key={file.id}>
                  <FileIcon className="file-icon" width={24} height={24} />

                  <FileInfo>
                    <FileName>{file.file.name}</FileName>
                    <FileSize>{formatFileSize(file.file.size)}</FileSize>
                  </FileInfo>
                  <FileActions>
                    {!file.disableRemoveButton && (
                      <RemoveButton className="trash-button" onClick={() => handleRemoveFile(file.id)}>
                        <Trash width={16} height={16} />
                      </RemoveButton>
                    )}
                  </FileActions>
                </FilePreviewItem>
              ))}
            </FilePreviewContainer>
          )}
        </UploadArea>

        <input
          hidden
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFileSelect(e.target.files)}
        />
      </Container>
    );
  },
);
